import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import type { PreviewSessionCursorDto } from "../../api/generated.ts";
import { controlPlaneApi } from "../../api/client.ts";

export const brandingQueryKeys = {
  accessContext: ["access-context"] as const,
  workspaces: ["workspaces"] as const,
  aggregate: (workspaceId?: string) => ["branding", workspaceId, "aggregate"] as const,
  versions: (workspaceId?: string) => ["branding", workspaceId, "versions"] as const,
  targets: (workspaceId?: string) => ["branding", workspaceId, "preview-targets"] as const,
  sessions: (workspaceId?: string) => ["branding", workspaceId, "preview-sessions"] as const,
  audit: (workspaceId?: string) => ["branding", workspaceId, "audit"] as const,
  organizationAudit: (organizationId?: string) => ["branding", organizationId, "organization-audit"] as const,
  systemBrandings: ["system-brandings"] as const,
  systemBranding: (templateId: string, version: number) => ["system-brandings", templateId, version] as const,
  mcpTokens: (workspaceId?: string) => ["branding", workspaceId, "mcp-tokens"] as const,
  runtimeCredentials: (workspaceId?: string) => ["branding", workspaceId, "runtime-credentials"] as const,
};

export function useAccessContext() {
  return useQuery({
    queryKey: brandingQueryKeys.accessContext,
    queryFn: () => controlPlaneApi.getAccessContext(),
    staleTime: Number.POSITIVE_INFINITY,
  });
}

export function useWorkspaces() {
  return useQuery({ queryKey: brandingQueryKeys.workspaces, queryFn: () => controlPlaneApi.listWorkspaces() });
}

export function useBrandingAggregate(workspaceId?: string) {
  return useQuery({
    queryKey: brandingQueryKeys.aggregate(workspaceId),
    queryFn: () => controlPlaneApi.getBranding(workspaceId ?? ""),
    enabled: Boolean(workspaceId),
  });
}

export function useBrandingVersions(workspaceId?: string) {
  return useQuery({
    queryKey: brandingQueryKeys.versions(workspaceId),
    queryFn: () => controlPlaneApi.listVersions(workspaceId ?? "", true),
    enabled: Boolean(workspaceId),
    refetchInterval: (query) => query.state.data?.some((version) => version.state === "publishing") ? 2_000 : false,
  });
}

export function useSystemBrandings(enabled = true) {
  return useQuery({
    queryKey: brandingQueryKeys.systemBrandings,
    queryFn: () => controlPlaneApi.listSystemBrandings(),
    enabled,
    staleTime: 60_000,
  });
}

export function usePreviewTargets(workspaceId?: string) {
  return useQuery({
    queryKey: brandingQueryKeys.targets(workspaceId),
    queryFn: () => controlPlaneApi.listPreviewTargets(workspaceId ?? ""),
    enabled: Boolean(workspaceId),
  });
}

export function usePreviewSessions(workspaceId?: string) {
  return useInfiniteQuery({
    queryKey: brandingQueryKeys.sessions(workspaceId),
    queryFn: ({ pageParam }) => controlPlaneApi.listPreviewSessions(workspaceId ?? "", {
      limit: 25,
      ...(pageParam ? { before: pageParam } : {}),
    }),
    initialPageParam: null as PreviewSessionCursorDto | null,
    getNextPageParam: (lastPage) => lastPage.nextCursor,
    enabled: Boolean(workspaceId),
  });
}

export function useWorkspaceAudit(workspaceId?: string) {
  return useQuery({
    queryKey: brandingQueryKeys.audit(workspaceId),
    queryFn: () => controlPlaneApi.listAudit(workspaceId ?? ""),
    enabled: Boolean(workspaceId),
  });
}

export function useWorkspaceMcpTokens(workspaceId?: string) {
  return useQuery({
    queryKey: brandingQueryKeys.mcpTokens(workspaceId),
    queryFn: () => controlPlaneApi.listWorkspaceMcpTokens(workspaceId ?? ""),
    enabled: Boolean(workspaceId),
  });
}

export function useWorkspaceRuntimeCredentials(workspaceId?: string) {
  return useQuery({
    queryKey: brandingQueryKeys.runtimeCredentials(workspaceId),
    queryFn: () => controlPlaneApi.listWorkspaceRuntimeCredentials(workspaceId ?? ""),
    enabled: Boolean(workspaceId),
  });
}

export function useOrganizationAudit(organizationId?: string) {
  return useQuery({
    queryKey: brandingQueryKeys.organizationAudit(organizationId),
    queryFn: () => controlPlaneApi.listOrganizationAudit(organizationId ?? ""),
    enabled: Boolean(organizationId),
  });
}
